import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Alert, Spinner,
    Modal, InputGroup, Badge
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import './Factures.css';

export default function EnvoyerEmailDevis() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [devis, setDevis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    
    // État du formulaire d'email
    const [emailForm, setEmailForm] = useState({
        destinataire: '',
        cc: '',
        bcc: '',
        sujet: '',
        message: '',
        inclurePdf: true
    });

    useEffect(() => {
        fetchDevis();
    }, [id]);

    const fetchDevis = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/devis.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setDevis(result.data);
            
            // Pré-remplir l'email avec les infos du client
            if (result.data) {
                setEmailForm({
                    ...emailForm,
                    destinataire: result.data.client_email || '',
                    sujet: `Devis ${result.data.numero_proforma} - ${result.data.client_nom}`,
                    message: generateDefaultMessage(result.data)
                });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const generateDefaultMessage = (devis) => {
        const today = new Date().toLocaleDateString('fr-FR');
        const validite = formatDate(devis.date_validite);
        
        return `Bonjour,

Nous avons le plaisir de vous adresser notre devis n° ${devis.numero_proforma} pour les prestations demandées.

Vous trouverez en pièce jointe le détail de notre proposition.

Ce devis est valable jusqu'au ${validite}. Restant à votre disposition pour tout renseignement complémentaire, nous vous prions d'agréer, Madame, Monsieur, l'expression de nos salutations distinguées.

Cordialement,
L'équipe GESTION STOCK`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!emailForm.destinataire) {
            setError("Veuillez saisir une adresse email");
            return;
        }

        setSending(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;

            // Simulation d'envoi d'email (à remplacer par votre API d'envoi)
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/send-email.php',
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        to: emailForm.destinataire,
                        cc: emailForm.cc,
                        bcc: emailForm.bcc,
                        subject: emailForm.sujet,
                        message: emailForm.message,
                        devis_id: id,
                        include_pdf: emailForm.inclurePdf
                    })
                }
            );

            // Simuler un délai d'envoi
            await new Promise(resolve => setTimeout(resolve, 1500));

            setSuccess("Email envoyé avec succès !");
            setTimeout(() => {
                navigate(`/ventes/DetailDevis/${id}`);
            }, 2000);

        } catch (err) {
            setError("Erreur lors de l'envoi de l'email");
        } finally {
            setSending(false);
        }
    };

    const getStatutBadge = (statut) => {
        const configs = {
            brouillon: { color: 'secondary', label: 'Brouillon' },
            envoye: { color: 'primary', label: 'Envoyé' },
            accepte: { color: 'success', label: 'Accepté' },
            refuse: { color: 'danger', label: 'Refusé' },
            converti_facture: { color: 'info', label: 'Converti' }
        };
        const config = configs[statut] || configs.brouillon;
        return <Badge bg={config.color}>{config.label}</Badge>;
    };

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if (!devis) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>Devis non trouvé</h4>
                        <Button 
                            variant="outline-danger" 
                            className="mt-3"
                            onClick={() => navigate('/ventes/devis')}
                        >
                            <i className="ri-arrow-left-line me-2"></i>
                            Retour aux devis
                        </Button>
                    </Alert>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="page-icon bg-warning-soft">
                                        <i className="ri-mail-send-line text-warning"></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1">Envoyer le devis par email</h1>
                                        <p className="page-subtitle mb-0">
                                            {devis.numero_proforma} - {devis.client_nom} • {getStatutBadge(devis.statut)}
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => navigate(`/ventes/DetailDevis/${id}`)}
                                        className="px-4 py-2"
                                    >
                                        <i className="ri-arrow-left-line me-2"></i>
                                        Retour
                                    </Button>
                                    <Button 
                                        variant="outline-info" 
                                        onClick={() => setShowPreviewModal(true)}
                                        className="px-4 py-2"
                                    >
                                        <i className="ri-eye-line me-2"></i>
                                        Aperçu
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2"></i>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success" className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2"></i>
                            {success}
                        </Alert>
                    )}

                    <Row>
                        {/* Formulaire d'envoi */}
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Header className="bg-primary text-white">
                                    <h5 className="mb-0">
                                        <i className="ri-mail-send-line me-2"></i>
                                        Composition de l'email
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <Form onSubmit={handleSubmit}>
                                        <Row className="g-3">
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Destinataire <span className="text-danger">*</span></Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text>
                                                            <i className="ri-user-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            value={emailForm.destinataire}
                                                            onChange={(e) => setEmailForm({...emailForm, destinataire: e.target.value})}
                                                            placeholder="client@email.com"
                                                            required
                                                        />
                                                    </InputGroup>
                                                    <Form.Text className="text-muted">
                                                        Email du client
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>CC</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text>
                                                            <i className="ri-user-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            value={emailForm.cc}
                                                            onChange={(e) => setEmailForm({...emailForm, cc: e.target.value})}
                                                            placeholder="cc@email.com"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label>BCC</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text>
                                                            <i className="ri-user-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="email"
                                                            value={emailForm.bcc}
                                                            onChange={(e) => setEmailForm({...emailForm, bcc: e.target.value})}
                                                            placeholder="bcc@email.com"
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Sujet</Form.Label>
                                                    <InputGroup>
                                                        <InputGroup.Text>
                                                            <i className="ri-subject-line"></i>
                                                        </InputGroup.Text>
                                                        <Form.Control
                                                            type="text"
                                                            value={emailForm.sujet}
                                                            onChange={(e) => setEmailForm({...emailForm, sujet: e.target.value})}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Message</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={8}
                                                        value={emailForm.message}
                                                        onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                                                        style={{ fontFamily: 'Arial, sans-serif' }}
                                                        required
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="include-pdf"
                                                        label="Joindre le devis au format PDF"
                                                        checked={emailForm.inclurePdf}
                                                        onChange={(e) => setEmailForm({...emailForm, inclurePdf: e.target.checked})}
                                                    />
                                                </Form.Group>
                                            </Col>

                                            <Col md={12}>
                                                <hr />
                                                <div className="d-flex justify-content-end gap-2">
                                                    <Button 
                                                        variant="secondary" 
                                                        onClick={() => navigate(`/ventes/DetailDevis/${id}`)}
                                                    >
                                                        Annuler
                                                    </Button>
                                                    <Button 
                                                        type="submit" 
                                                        variant="warning" 
                                                        disabled={sending}
                                                        className="px-5"
                                                    >
                                                        {sending ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Envoi en cours...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-send-plane-line me-2"></i>
                                                                Envoyer
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Informations du devis */}
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Header className="bg-primary text-white">
                                    <h5 className="mb-0">
                                        <i className="ri-information-line me-2"></i>
                                        Récapitulatif
                                    </h5>
                                </Card.Header>
                                <Card.Body>
                                    <div className="mb-3">
                                        <small className="text-muted d-block">Devis</small>
                                        <h5>{devis.numero_proforma}</h5>
                                    </div>

                                    <div className="mb-3">
                                        <small className="text-muted d-block">Client</small>
                                        <h6 className="mb-1">{devis.client_nom}</h6>
                                        <p className="text-muted small mb-0">Code: {devis.code_client}</p>
                                    </div>

                                    <div className="mb-3">
                                        <small className="text-muted d-block">Montant total</small>
                                        <h4 className="text-primary">{formatCurrency(devis.net_a_payer)}</h4>
                                    </div>

                                    <div className="mb-3">
                                        <small className="text-muted d-block">Validité</small>
                                        <p className="mb-0">{formatDate(devis.date_validite)}</p>
                                    </div>

                                    <hr />

                                    <div className="mb-3">
                                        <small className="text-muted d-block">Statut</small>
                                        <div className="mt-1">
                                            {getStatutBadge(devis.statut)}
                                        </div>
                                    </div>

                                    {devis.notes && (
                                        <>
                                            <hr />
                                            <div>
                                                <small className="text-muted d-block">Notes</small>
                                                <p className="small mt-1">{devis.notes}</p>
                                            </div>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>

                            <Card className="border-0 shadow-sm bg-light">
                                <Card.Body>
                                    <h6 className="mb-3">
                                        <i className="ri-information-line text-primary me-2"></i>
                                        Informations
                                    </h6>
                                    <ul className="small text-muted ps-3 mb-0">
                                        <li className="mb-2">L'email sera envoyé avec le devis en pièce jointe</li>
                                        <li className="mb-2">Le client recevra une copie du devis</li>
                                        <li className="mb-2">Le statut du devis sera automatiquement mis à jour en "Envoyé"</li>
                                    </ul>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            {/* Modal d'aperçu de l'email */}
            <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="lg" centered className="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="ri-eye-line me-2 text-primary"></i>
                        Aperçu de l'email
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="email-preview">
                        <div className="mb-3 p-3 bg-light rounded">
                            <div className="row mb-2">
                                <div className="col-3 text-muted">De :</div>
                                <div className="col-9">GESTION STOCK &lt;contact@gestionstock.ga&gt;</div>
                            </div>
                            <div className="row mb-2">
                                <div className="col-3 text-muted">À :</div>
                                <div className="col-9">{emailForm.destinataire || 'Non spécifié'}</div>
                            </div>
                            {emailForm.cc && (
                                <div className="row mb-2">
                                    <div className="col-3 text-muted">CC :</div>
                                    <div className="col-9">{emailForm.cc}</div>
                                </div>
                            )}
                            {emailForm.bcc && (
                                <div className="row mb-2">
                                    <div className="col-3 text-muted">BCC :</div>
                                    <div className="col-9">{emailForm.bcc}</div>
                                </div>
                            )}
                            <div className="row mb-2">
                                <div className="col-3 text-muted">Sujet :</div>
                                <div className="col-9 fw-bold">{emailForm.sujet}</div>
                            </div>
                        </div>

                        <div className="email-content p-3 border rounded" style={{ whiteSpace: 'pre-wrap' }}>
                            {emailForm.message}
                        </div>

                        {emailForm.inclurePdf && (
                            <div className="mt-3 p-2 bg-light rounded d-flex align-items-center">
                                <i className="ri-file-pdf-line text-danger fs-4 me-2"></i>
                                <span>Devis_{devis?.numero_proforma}.pdf</span>
                                <Badge bg="success" className="ms-auto">Joint</Badge>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    );
}